import { useRef, useEffect, useState, memo, useCallback } from 'react';
import type { PDFDocumentProxy } from '../../lib/pdfWorker';
import { PDF_VIEWER } from './pdfViewerTokens';

import type { ViewerMode } from './signingTypes';

interface ThumbnailPageProps {
  document: PDFDocumentProxy;
  pageNumber: number;
  isActive: boolean;
  fieldCount: number;
  completedCount?: number;
  mode?: ViewerMode;
  onPageClick: (page: number) => void;
}

const THUMB_SCALE = 0.5;

const ThumbnailPage = memo(function ThumbnailPage({
  document, pageNumber, isActive, fieldCount, completedCount = 0, mode = 'sign', onPageClick,
}: ThumbnailPageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderedRef = useRef(false);
  const [dims, setDims] = useState({ w: PDF_VIEWER.thumbWidth, h: PDF_VIEWER.thumbHeight });

  const handleClick = useCallback(() => onPageClick(pageNumber), [onPageClick, pageNumber]);

  useEffect(() => {
    if (renderedRef.current || !document || !canvasRef.current) return;
    renderedRef.current = true;

    (async () => {
      try {
        const page = await document.getPage(pageNumber);
        const viewport = page.getViewport({ scale: THUMB_SCALE });
        const canvas = canvasRef.current;
        if (!canvas) return;

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        // Set display dims via React state so they persist across re-renders
        setDims({ w: PDF_VIEWER.thumbWidth, h: PDF_VIEWER.thumbHeight });

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        await page.render({ canvasContext: ctx, viewport, canvas } as any).promise;
      } catch {
        // ignore render errors on unmounted canvas
      }
    })();
  }, [document, pageNumber]);

  return (
    <div
      onClick={handleClick}
      style={{
        cursor: 'pointer',
        borderRadius: 6,
        border: `2px solid ${isActive ? PDF_VIEWER.fieldActive : 'transparent'}`,
        overflow: 'hidden',
        backgroundColor: PDF_VIEWER.bgSheet,
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: isActive ? `0 0 0 1px ${PDF_VIEWER.fieldActive}44` : '0 1px 3px rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      {/* Canvas area with field badge */}
      <div style={{ position: 'relative', padding: '10px 10px 0 10px' }}>
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: dims.w,
            height: dims.h,
            borderRadius: 3,
            objectFit: 'cover',
            objectPosition: 'top',
          }}
        />

        {/* Field count badge — top-right corner of canvas */}
        {fieldCount > 0 && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
            backgroundColor: (mode === 'sign' && completedCount === fieldCount) ? PDF_VIEWER.fieldSigned : PDF_VIEWER.fieldUnsigned,
            color: '#fff',
            fontSize: 9, fontWeight: 600, borderRadius: 8,
            padding: '1px 6px', lineHeight: '16px',
            minWidth: 16, textAlign: 'center',
          }}>
            {mode === 'sign' ? `${completedCount}/${fieldCount}` : fieldCount}
          </div>
        )}
      </div>

      {/* Page number label — below canvas */}
      <div style={{
        padding: '10px 0 12px',
        textAlign: 'center',
        fontSize: 11, fontWeight: 600, lineHeight: '16px',
        color: isActive ? PDF_VIEWER.fieldActive : 'rgba(0,0,0,0.5)',
        transition: 'color 0.15s',
      }}>
        Page {pageNumber}
      </div>
    </div>
  );
});

export default ThumbnailPage;
